import { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import * as ExcelJS from 'exceljs';
import { Button } from '@mui/material';
import { styled } from '@mui/material';
import { ReactComponent as ExcelIcon } from '../../../../assets/icons/main/microsoft-excel-icon.svg';
import { minLength, maxLength, stringPattern } from '../../../../utils/form-validations';
import reduce from 'lodash/reduce';
import isEqual from 'lodash/isEqual';
import snakeCase from 'lodash/snakeCase';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;
/**
 * excelschema is required to match with the excel columns
 * excelschema should contain key value pair, we are checking type of value, string is supported for now
 */
const fileTypes = ['xlsx', 'csv'];
const ExcelImport = (props: any) => {
  const {
    excelSchema,
    style,
    data,
    uniqueParams,
    dataColumns,
    getRowsOnly,
    onGetRows = () => {},
    onGetError = () => {},
  } = props;
  const [extractedData, setExtractedData] = useState<any[]>([]);
  const [loading, setLoading] = useState({ isLoading: false, message: '', success: false });

  const uploadFile = (e: any) => {
    setExtractedData([]);
    let files = e.target.files;
    if (files.length) {
      let fileData = e.target.files[0];
      let extension = files[0].name.split('.').pop().toLowerCase(),
        isSuccess = fileTypes.indexOf(extension) > -1;

      if (isSuccess) {
        setLoading({ isLoading: true, message: 'Loading Data', success: false });
        const fileReader = new FileReader();
        fileReader.onload = (e: any) => {
          if (e.target.result) {
            const dataBuffer = e.target.result;
            const workbook = new ExcelJS.Workbook();
            if (extension === 'csv') {
              parseCSVData(dataBuffer);
            } else {
              workbook.xlsx
                .load(dataBuffer)
                .then(() => {
                  let excelTitles: any = [];
                  let excelData: any = [];
                  let metaTags: any = [];
                  let _excelSchema = dataColumns;
                  if (workbook.worksheets[0]) {
                    workbook.worksheets[0].eachRow((row: any, rowNumber: any) => {
                      if (rowNumber === 1) {
                        const titlesRow = row.values
                          .slice(1, row.values.length)
                          .map((value: string) => snakeCase(value));
                        if (!isEqual(dataColumns, titlesRow)) {
                          return onGetError({
                            message: 'Please add columns properly!'
                          })
                        }
                      }
                      if (rowNumber > 0) {
                        let rowValues: any = row.values;
                        rowValues.shift();
                        if (rowNumber === 1) excelTitles = rowValues;
                        else {
                          let rowObject: any = {};
                          for (let i = 0; i < excelTitles.length; i++) {
                            let title = _excelSchema[i]; 
                            let value = rowValues[i] ? rowValues[i] : '';
                            if (i > _excelSchema.length - 1) {
                              title = excelTitles[i].replace(' ', '_').toLowerCase();
                            }
                            if (typeof value === 'number') {
                              rowObject[title] = value.toString();
                            } else {
                              rowObject[title] = value;
                            }
                          }
                          rowObject['id'] = rowNumber - 1;
                          excelData.push(rowObject);
                        }
                      } else {
                        onGetError({
                          message: 'Excel file does not contain any row. Please check and try again.',
                        });
                        setLoading({
                          isLoading: false,
                          message: 'Excel file does not contain any row. Please check and try again.',
                          success: false,
                        });
                      }
                    });

                    const _resCheckHeader = checkHeader(excelTitles);
                    if (_resCheckHeader.success) {
                      if (excelData.length > 0) {
                        setExtractedData(
                          excelData.map((item: any, index: number) => {
                            let _hasError: boolean = validateString(item);
                            if (uniqueParams && data && data.length && uniqueParams.length) {
                              for (let keys of uniqueParams) {
                                if (item[keys]) {
                                  if (
                                    data.findIndex(
                                      (e: any) => e[keys].toLowerCase() === item[keys].toString().toLowerCase().trim()
                                    ) > -1
                                  ) {
                                    return { ...item, hasError: true };
                                  }
                                }
                              }
                            }
                            return { ...item, hasError: _hasError };
                          })
                        );
                        setTimeout(() => {
                          setLoading({ isLoading: false, message: '', success: true });
                        }, 1000);
                      } else {
                        setExtractedData([]);
                        onGetError({
                          message: 'It looks like you dont have data in excel file, please check and try again.',
                        });
                        setLoading({
                          isLoading: false,
                          message: 'It looks like you dont have data in excel file, please check and try again.',
                          success: false,
                        });
                      }
                    } else {
                      setLoading(_resCheckHeader);
                    }
                  } else {
                    onGetError({
                      message: 'Excel does not contain any sheets, please check and try again.',
                    });
                    setLoading({
                      isLoading: false,
                      message: 'Excel does not contain any sheets, please check and try again.',
                      success: false,
                    });
                  }
                })
                .catch((err: any) => {
                  setExtractedData([]);
                  onGetError({
                    message:
                      'Not able to read excel file, Please check file format, for reference download template and try again. [' +
                      err.message +
                      ']',
                  });
                  setLoading({
                    isLoading: false,
                    message:
                      'Not able to read excel file, Please check file format, for reference download template and try again. [' +
                      err.message +
                      ']',
                    success: false,
                  });
                });
            }
          } else {
            onGetError({ message: 'Not able to read ' + fileData.name + ', Please check format and try again' });
            setLoading({
              isLoading: false,
              message: 'Not able to read ' + fileData.name + ', Please check format and try again',
              success: false,
            });
          }
        };
        if (extension === 'csv') {
          fileReader.readAsText(fileData);
        } else {
          fileReader.readAsBinaryString(fileData);
        }
      } else {
        onGetError({ message: 'Not able to read ' + fileData.name + ', Please check format and try again' });
        setLoading({
          isLoading: false,
          message: 'File ' + fileData.name + ' is not supported, Please upload excel/csv',
          success: false,
        });
      }
    } else {
      onGetError({ message: 'Not able to find uploaded file, Please try again' });
      setLoading({ isLoading: false, message: 'Not able to find uploaded file, Please try again', success: false });
    }
  };

  const parseCSVData = (str: string, delim = ';') => {
    delim = str.slice(0, str.indexOf('\n')).indexOf(',') > -1 ? ',' : ';';
    if (delim === ',' || delim === ';') {
      const _headers = str
        .slice(0, str.indexOf('\n'))
        .split(delim)
        .filter((item) => item.trim().length > 0);
      const _resCheckHeader = checkHeader(_headers);
      if (_resCheckHeader.success) {
        const _rows = str
          .slice(str.indexOf('\n') + 1)
          .split('\n')
          .filter((item) => item.trim().length > 0);
        const _csvModifiedData = _rows.map((row: any, index: number) => {
          const values = row.split(delim);
          const eachObject = _headers.reduce((obj: any, header, i) => {
            obj[header.trim().replace(' ', '_').toLowerCase()] = values[i].trim();
            obj['id'] = index + 1;
            return obj;
          }, {});

          return eachObject;
        });
        if (_csvModifiedData && _csvModifiedData.length > 0) {
          setExtractedData(
            _csvModifiedData.map((item: any) => {
              let _hasError = validateString(item);
              if (data.length && uniqueParams.length) {
                for (let keys of uniqueParams) {
                  if (item[keys]) {
                    if (
                      data.findIndex((e: any) => e[keys].toLowerCase() === item[keys].toString().toLowerCase().trim()) >
                      -1
                    ) {
                      return { ...item, hasError: true };
                    }
                  }
                }
              }
              return { ...item, hasError: _hasError };
            })
          );
          setTimeout(() => {
            setLoading({ isLoading: false, message: '', success: true });
          }, 1000);
        } else {
          setExtractedData([]);
          setLoading({
            isLoading: false,
            message: 'CSV file does not contain any data, please check and upload again.',
            success: false,
          });
        }
      } else {
        setLoading(_resCheckHeader);
      }
    } else {
      setLoading({
        isLoading: false,
        message: 'CSV file is not valid or not maching with the requied format, please check template for reference.',
        success: false,
      });
    }
  };

  const checkHeader = (header: any) => {
    const _header = header.map((item: any) => item.trim().replace(' ', '_').toLowerCase());
    if (_header.length) {
      if (_header.includes('id') || _header.includes('ID') || _header.includes('Id')) {
        return {
          isLoading: false,
          message: 'Colums are not allowed with id keyword, please change it with alternative name',
          success: false,
        };
      } else {
        const _schemaData: any =
          Object.keys(excelSchema).length > 0
            ? Object.keys(excelSchema).filter((item: any) => typeof excelSchema[item] == 'string')
            : [];

        if (_header.length >= _schemaData.length - 1) {
          const contains = _header.every((element: any, index: any) => {
            if (index < _schemaData.length) {
              return _schemaData.indexOf(element) !== -1;
            } else {
              return true;
            }
          });

          if (true) {
            return { isLoading: false, message: '', success: true };
          } else {
            return {
              isLoading: false,
              message: 'Headers are not matching with template, please check & try again',
              success: false,
            };
          }
        } else {
          return {
            isLoading: false,
            message: 'Colums are not as per template, please check & try again',
            success: false,
          };
        }
      }
    } else {
      return {
        isLoading: false,
        message: 'Headers are not present, please check template and try again',
        success: false,
      };
    }
  };

  const validateString = (item: any) => {
    const validateItemList: any = reduce(
      item,
      (acc: any, value: any, key: any) => {
        if (Object.keys(excelSchema).includes(key)) {
          acc[key] = value;
        }
        return {
          ...acc,
        };
      },
      {}
    );
    return Object.values(validateItemList).some((vals: any) => {
      return vals.length < minLength || vals.length > maxLength;
    });
  };

  const changeSchemaBuilderValue = (e: any, row: any) => {
    const { name, value } = e.target;
    const updatedSchema = extractedData.map((schema: any) => {
      if (schema.id === row.id) {
        return {
          ...schema,
          [name]: value,
        };
      }
      return schema;
    });

    setExtractedData(
      updatedSchema.map((item: any, index: number) => {
        if (row.id === index + 1) {
          let _hasError = validateString(item);
          if (uniqueParams && data && data.length && uniqueParams.length) {
            for (let keys of uniqueParams) {
              if (item[keys]) {
                if (
                  data.findIndex((e: any) => e[keys].toLowerCase() === item[keys].toString().toLowerCase().trim()) > -1
                ) {
                  return { ...item, hasError: true };
                }
              }
            }
          }
          return { ...item, hasError: _hasError };
        } else {
          return { ...item };
        }
      })
    );
  };

  const handleRenderCell = (params: any) => {
    let regPattern = new RegExp(stringPattern);
    let error: boolean = false;
    let message: string = '';
    if (Object.keys(excelSchema).includes(params.field)) {
      if (typeof params.value === 'string' || typeof params.value === 'number') {
        if (params.value.trim().length === 0) {
          error = true;
          message = 'Field is blank';
        } else if (params.value.length < minLength) {
          error = true;
          message = 'Min length is ' + minLength;
        } else if (params.value.length > maxLength) {
          error = true;
          message = 'Mxn length is ' + maxLength;
        } else if (!regPattern.test(params.value)) {
          error = true;
          message = 'Should not contain special character';
        } else {
          if (uniqueParams && uniqueParams.length && data && data.length) {
            if (uniqueParams.includes(params.field)) {
              if (
                data.findIndex((e: any) => e[params.field].toLowerCase() === params.value.toLowerCase().trim()) > -1
              ) {
                error = true;
                message = 'Duplicate field';
              } else {
                error = false;
                message = '';
              }
            } else {
              error = false;
              message = '';
            }
          } else {
            error = false;
            message = '';
          }
        }
      } else {
        error = true;
        message = 'Should not contain special character';
      }
    }

    return (
      <TextField
        variant='outlined'
        size='small'
        name={params?.field}
        value={extractedData.find((schema: any) => schema.id === params.id)[params.field]}
        onChange={(e: any) => changeSchemaBuilderValue(e, params?.row)}
        error={error}
        onKeyDown={(event: any) => {
          event.stopPropagation();
        }}
        helperText={error && message}
        sx={{
          '.MuiOutlinedInput-root': {
            fontSize: '1rem',
            marginTop: error ? '1rem' : '',
          },
          '.MuiFormHelperText-root': {
            color: 'rgba(255,0,0,0.5) !important',
            marginTop: '2px !important',
            marginLeft: '5px !important',
            fontSize: '0.8rem !important',
            lineHeight: '1 !important',
            marginBottom: '5px',
          },
        }}
      />
    );
  };

  useEffect(() => {
    if (getRowsOnly && extractedData.length) {
      onGetRows(extractedData);
    }
  }, [getRowsOnly, extractedData.length]);

  return (
    <Button
      variant='outlined'
      startIcon={!getRowsOnly && <ExcelIcon {...(!props.hasAccess && { style: {} })} />}
      component='label'
      sx={{ ...style, height: '42px' }}
      disabled={!props.hasAccess}
    >
      {getRowsOnly ? 'BROWSE' : 'Import Excel / CSV'}
      <VisuallyHiddenInput
        type='file'
        onChange={uploadFile}
        onClick={(event: any) => {
          event.target.value = null;
        }}
      />
    </Button>
  );
};

export default ExcelImport;
